import { RadioButton, RadioButtonTrueFalse } from "Components/Form/RadioButton";
import {
  VeteranAutoProductIntroFormValues,
  VeteranAutoTexts,
} from "../veteranAutoInterface";
import {
  getVeteranAutoTypeOptions,
  mapVeteranModelsToOptions,
} from "../veteranAutoUtils";

import { WrapMdMarginBottom } from "App/global-style";
import { ContactUsAlert } from "Components/Form/Alert";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { Select } from "Components/Form/Select";
import { GetVeteranVehicleModelsEntity } from "Store/getVeteranVehicleModel/interfaces";
import { getVeteranVehicleModelStart } from "Store/getVeteranVehicleModel/slice";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { VeteranAutoType } from "Store/interfaces/products/checkPrice/riskProducts/VehicleProducts/VeteranAuto";
import { TextLanguage } from "Store/texts/interfaces";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { FormikProps } from "formik";
import React from "react";
import { useDispatch } from "react-redux";
import { getValidVeteranAutoRegistrationYearOptions } from "../../vehicleUtils";

interface Props {
  formProps: FormikProps<VeteranAutoProductIntroFormValues>;
  texts: VeteranAutoTexts["formText"]["checkPrice"];
  lang: TextLanguage;
  getVeteranVehicleModel: GetVeteranVehicleModelsEntity;
}

export const FormElements = (props: Props): JSX.Element => {
  const { texts, lang, formProps, getVeteranVehicleModel } = props;
  const dispatch = useDispatch();

  const yearOptions = getValidVeteranAutoRegistrationYearOptions(
    formProps.values.modelAndValue,
  );
  // Temporary solution for placeholder as necessary logic
  // inside of Select requires a major bumb to alpha of 3.0.0
  const modelAndValuedPlaceholder = {
    value: "",
    label: texts.modelAndValue.placeholder?.[lang] ?? "",
  };
  const firstRegisteredPlaceholder = {
    value: "",
    label: texts.firstRegistered?.placeholder?.[lang] ?? "",
  };

  return (
    <>
      <RadioButtonTrueFalse
        name="isInGoodCondition"
        id="isInGoodCondition"
        label={texts.isInGoodCondition.label[lang]}
        hint={texts.isInGoodCondition.hint?.[lang]}
        options={[
          {
            label: texts.isInGoodCondition.options.yes[lang],
            value: true,
            id: "condition-good",
          },
          {
            label: texts.isInGoodCondition.options.no[lang],
            value: false,
            id: "condition-bad",
          },
        ]}
        direction="vertical"
        error={getError("isInGoodCondition", formProps)}
      />
      {formProps.values.isInGoodCondition === BooleanFormType.FALSE && (
        <WrapMdMarginBottom>
          <ContactUsAlert
            title={texts.isInGoodCondition.error.isTrue[lang]}
            alertId="isInGoodCondition"
          />
        </WrapMdMarginBottom>
      )}
      <RadioButton
        name="veteranAutoType"
        id="veteranAutoType"
        label={texts.veteranAutoType.label[lang]}
        options={getVeteranAutoTypeOptions()}
        hint={texts.veteranAutoType.hint?.[lang]}
        direction="vertical"
        error={getError("veteranAutoType", formProps)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          dispatch(
            getVeteranVehicleModelStart({
              vehicleType: e.target.value as VeteranAutoType,
            }),
          );
          formProps.setFieldValue("modelAndValue", undefined);
          formProps.setFieldTouched("modelAndValue", false);
        }}
      />
      {isDefined(getVeteranVehicleModel.error) && (
        <SystemErrorAlert alertId="getVeteranVehicleModelError" />
      )}
      <Select
        id="modelAndValue"
        name="modelAndValue"
        label={texts.modelAndValue.label[lang]}
        hint={texts.modelAndValue.hint?.[lang] ?? ""}
        options={[
          modelAndValuedPlaceholder,
          ...mapVeteranModelsToOptions(getVeteranVehicleModel.current),
        ]}
        error={getError("modelAndValue", formProps)}
        large
        disabled={!isDefined(getVeteranVehicleModel.current)}
        isLoading={getVeteranVehicleModel.isLoading}
      />
      <Input
        id="brandOrModel"
        name="brandOrModel"
        label={texts.brandOrModel.label[lang]}
        placeholder={texts.brandOrModel.placeholder?.[lang]}
        error={getError("brandOrModel", formProps)}
        large
        inputProps={{ maxLength: 40 }}
        inputMode={InputMode.TEXT}
      />
      <Select
        id="firstRegistered"
        name="firstRegistered"
        label={texts.firstRegistered.label[lang]}
        options={[firstRegisteredPlaceholder, ...yearOptions]}
        hint={texts.firstRegistered.hint?.[lang]}
        error={getError("firstRegistered", formProps)}
        large
      />
    </>
  );
};
